/**
 * View Branch - Display detailed information about a branch
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, AdminPageLayout, Badge, Section, Grid, Col } from '../../components';
import { MapPin, Phone, Mail, Globe, Activity, Users, Shield, Zap } from 'lucide-react';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const ViewBranch = () => {
  const navigate = useNavigate();
  const { branch_id } = useParams();
  const { showSuccess, showError } = useToast();

  const [branch, setBranch] = useState(null);
  const [stats, setStats] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadBranchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const branchData = await apiService.getBranch(branch_id);
      setBranch(branchData);

      try {
        const statsData = await apiService.getBranchStats(branch_id);
        setStats(statsData);
      } catch (err) {
        console.error('Error loading stats');
      }

      if (branchData.admin_user_id) {
        try {
          const adminData = await apiService.getUser(branchData.admin_user_id);
          setAdminUser(adminData);
        } catch (err) {
          console.error('Error loading admin user');
        }
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [branch_id]);

  useEffect(() => {
    loadBranchData();
  }, [loadBranchData]);

  const handleActivate = async () => {
    try {
      await apiService.activateBranch(branch_id);
      showSuccess(`Branch "${branch.name}" activated successfully`);
      loadBranchData();
    } catch (err) {
      showError('Failed to activate branch');
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteBranch(branch_id);
      showSuccess(`Branch "${branch.name}" deleted successfully`);
      setShowDeleteModal(false);
      navigate(`/admin/branches`);
    } catch (err) {
      showError('Failed to delete branch');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="SCRUTINIZE TERRITORY" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Synchronizing regional records...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout title="SCRUTINIZE TERRITORY" maxWidth="1400px">
        <Section title="Synchronisation Error">
          <Card level={1}>
            <p style={{ color: 'var(--color-error)', marginBottom: '1.5rem' }}>Failed to retrieve data: {error}</p>
            <Button onClick={loadBranchData} variant="primary">Attempt Resync</Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  if (!branch) {
    return (
      <AdminPageLayout title="SCRUTINIZE TERRITORY" maxWidth="1400px">
        <Section title="Territory Integrity">
          <Card level={1}>
            <p style={{ opacity: 0.6, marginBottom: '1.5rem' }}>The requested location does not exist in the active registry.</p>
            <Button onClick={() => navigate(-1)} variant="outline">
              Return to Catalog
            </Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  const locationTypeLabels = {
    main_facility: 'Main Facility',
    branch_facility: 'Branch Facility',
    training_ground: 'Training Ground',
    academy: 'Academy',
    community_center: 'Community Center'
  };

  return (
    <AdminPageLayout
      title="SCRUTINIZE TERRITORY"
      subtitle={branch.name}
      backPath={branch?.club_id ? `/admin/clubs/${branch.club_id}/branches` : "/admin/branches"}
      maxWidth="1400px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

        <Section
          title="Operational Status"
          actions={
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button
                onClick={() => navigate(`/admin/branches/${branch_id}/edit`)}
                variant="edit"
              >
                Modify Territory
              </Button>
              {branch.status === 'inactive' && (
                <Button
                  onClick={handleActivate}
                  variant="success"
                >
                  Activate Location
                </Button>
              )}
              <Button
                onClick={() => setShowDeleteModal(true)}
                variant="danger"
              >
                Decommission
              </Button>
            </div>
          }
        >
          <Card level={1}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'var(--surface-lvl2)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--color-border-light)'
              }}>
                <MapPin size={32} style={{ color: 'var(--color-primary)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>{branch.name}</div>
                <div style={{ opacity: 0.6, fontSize: '0.95rem' }}>{locationTypeLabels[branch.location_type]}</div>
              </div>
              <Badge variant={branch.status === 'active' ? 'success' : 'warning'} size="sm">
                {branch.status === 'active' ? 'OPERATIONAL' : branch.status.toUpperCase()}
              </Badge>
            </div>
          </Card>
        </Section>

        {stats && (
          <Grid columns={4} gap="1.5rem">
            <Col span={1}>
              <Card level={1} style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-primary)' }}>{stats.total_users || 0}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginTop: '0.5rem' }}>Total Ecosystem Users</div>
              </Card>
            </Col>
            <Col span={1}>
              <Card level={1} style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-primary)' }}>{stats.total_coaches || 0}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginTop: '0.5rem' }}>Active Mentors</div>
              </Card>
            </Col>
            <Col span={1}>
              <Card level={1} style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-primary)' }}>{stats.total_programs || 0}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginTop: '0.5rem' }}>Active Syllabi</div>
              </Card>
            </Col>
            <Col span={1}>
              <Card level={1} style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-primary)' }}>{stats.total_players || 0}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginTop: '0.5rem' }}>Participants</div>
              </Card>
            </Col>
          </Grid>
        )}

        <Grid columns={12} gap="2.5rem">
          <Col span={7}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
              <Section title="Territory Intelligence">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Focus & Mission</div>
                      <p style={{ lineHeight: 1.6, fontSize: '1.1rem' }}>{branch.description || 'No conceptual narrative provided for this location.'}</p>
                    </div>

                    <div style={{ padding: '1.5rem', background: 'var(--surface-lvl2)', borderRadius: '16px', border: '1px solid var(--color-border-light)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.5rem' }}>
                        <MapPin size={20} style={{ color: 'var(--color-primary)' }} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{branch.address_line1}</div>
                          {branch.address_line2 && <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>{branch.address_line2}</div>}
                          <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>{branch.city}, {branch.state} {branch.zip_code} • {branch.country}</div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Mail size={18} style={{ opacity: 0.6 }} />
                          <div style={{ fontSize: '0.9rem' }}>{branch.email}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <Phone size={18} style={{ opacity: 0.6 }} />
                          <div style={{ fontSize: '0.9rem' }}>{branch.phone}</div>
                        </div>
                        {branch.website && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', gridColumn: 'span 2' }}>
                            <Globe size={18} style={{ opacity: 0.6 }} />
                            <div style={{ fontSize: '0.9rem' }}>{branch.website}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </Section>

              <Section title="Governance Provisions">
                <Card level={1}>
                  <Grid columns={2} gap="2rem">
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Age Calculation</div>
                      <div style={{ fontWeight: 600 }}>{branch.age_calculation_method.replace(/_/g, ' ').toUpperCase()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Operational Currency</div>
                      <div style={{ fontWeight: 600 }}>{branch.currency}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Regional Timezone</div>
                      <div style={{ fontWeight: 600 }}>{branch.timezone}</div>
                    </div>
                  </Grid>
                </Card>
              </Section>
            </div>
          </Col>

          <Col span={5}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
              <Section title="Administrative Authority">
                <Card level={1}>
                  {adminUser ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                        <div style={{ width: '48px', height: '48px', background: 'var(--surface-lvl2)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyCenter: 'center' }}>
                          <Shield size={24} style={{ color: 'var(--color-primary)' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{adminUser.first_name} {adminUser.last_name}</div>
                          <div style={{ fontSize: '0.85rem', opacity: 0.6 }}>Branch Administrator</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '1.25rem', borderTop: '1px solid var(--color-border-light)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.9rem' }}>
                          <Mail size={16} style={{ opacity: 0.5 }} />
                          <span>{adminUser.email}</span>
                        </div>
                        {adminUser.phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.9rem' }}>
                            <Phone size={16} style={{ opacity: 0.5 }} />
                            <span>{adminUser.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '1rem', textAlign: 'center', opacity: 0.6 }}>
                      <Zap size={24} style={{ marginBottom: '0.5rem' }} />
                      <div style={{ fontSize: '0.9rem' }}>No dedicated authority provisioned. Under club governance.</div>
                    </div>
                  )}
                </Card>
              </Section>

              <Section title="Ecosystem Metadata">
                <Card level={1}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <Activity size={18} style={{ opacity: 0.5 }} />
                      <div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Provisioned At</div>
                        <div style={{ fontWeight: 600 }}>{new Date(branch.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <Users size={18} style={{ opacity: 0.5 }} />
                      <div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>Primary Registrar</div>
                        <div style={{ fontWeight: 600 }}>{branch.created_by}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Section>
            </div>
          </Col>
        </Grid>
      </div>

      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Decommission Territory"
        message={`Are you sure you want to decommission "${branch?.name}"?`}
        details="This action is irreversible and will purge all underlying operational data for this location."
        variant="danger"
        confirmText="Finalise Decommission"
        cancelText="Cancel"
        loading={isDeleting}
      />
    </AdminPageLayout>
  );
};

export default ViewBranch;
